import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, InfoOutlined as InfoIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import usePatchRequest from "../../hooks/usePatchRequest";
import { usePageMeta } from "../../contexts/PageMetaContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import { StatusChip } from "../../components/status";
import SearchInput from "../../components/SearchInput";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import FileUploadPreSignedUrl from "../../components/FileUpload/FileUploadPreSignedUrl";
import { INDIGO, SLATE } from "../../constants/Colors";
import {
  FILES_CLOUDFRONT_BASE_URL,
  FILES_COMPLETE_UPLOAD_ENDPOINT,
  FILES_GET_ENDPOINT,
  FILES_PRESIGNED_URL_ENDPOINT,
} from "../../constants/Constants";

const COL_TEMPLATE = "2fr 2fr 160px minmax(160px, 1fr) 130px 48px";

export default function Files({ agentContext = false }) {
  const { id: agentId } = useParams();
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { setPageMeta } = usePageMeta();

  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTagsFileId, setEditingTagsFileId] = useState(null);
  const [editingTags, setEditingTags] = useState([]);

  const hasLoadedOnce = useRef(false);
  const uploadTriggerRef = useRef(null);

  // Fetching files data from the API (pass agent_id for backend filtering when in agent context)
  const { data, loading, error } = useFetch(
    `${FILES_GET_ENDPOINT}?app_id=${currentApp.id}${agentId ? `&agent_id=${agentId}` : ""}`,
    tokens,
    [refreshKey, agentId]
  );

  // Initialize the usePatchRequest hook
  const { sendPatchRequest } = usePatchRequest(tokens);

  // Set page meta: description
  useEffect(() => {
    setPageMeta({ description: "Manage uploaded PDF files used as knowledge sources for this agent." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register "+ Add Files" button in TopBar — only when inside an agent context
  useEffect(() => {
    if (!agentId) return;
    setPageMeta((prev) => ({
      ...prev,
      actions: (
        <Button
          variant="contained"
          onClick={() => uploadTriggerRef.current?.()}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", px: 2, "&:hover": { boxShadow: "none" } }}
        >
          + Add Files
        </Button>
      ),
    }));
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to refresh data
  const refreshData = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Function to handle saving tags
  const handleSaveTags = async (fileId, newTags) => {
    if (!Array.isArray(newTags)) {
      newTags = [newTags];
    }
    const cleanedTags = newTags.map((tag) =>
      tag.toString().trim().replace(/^['\"]+|['\"]+$/g, "")
    );
    try {
      await sendPatchRequest(`/v1/files/${fileId}`, {
        app_id: currentApp.id,
        tags: cleanedTags,
      });
      refreshData();
    } catch (err) {
      console.error("Error updating tags:", err);
    }
  };


  const handleLlmExtractionToggle = (fileId, currentValue, url) => {
    setSelectedFile({ id: fileId, currentValue, url });
    setConfirmDialogOpen(true);
  };

  const confirmLlmExtractionToggle = async () => {
    if (!selectedFile) return;
    try {
      await sendPatchRequest(`/v1/files/${selectedFile.id}`, {
        app_id: currentApp.id,
        use_llm_extraction: !selectedFile.currentValue,
        url: selectedFile.url,
      });
      refreshData();
      setConfirmDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Error updating LLM extraction:", err);
      setConfirmDialogOpen(false);
      setSelectedFile(null);
    }
  };

  const cancelLlmExtractionToggle = () => {
    setConfirmDialogOpen(false);
    setSelectedFile(null);
  };

  const handleUploadComplete = () => {
    refreshData();
  };

  const allFiles = data?.files || [];

  const filteredFiles = searchQuery.trim()
    ? allFiles.filter((f) =>
        f.file_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.url?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allFiles;

  const tagOptions = ["sandbox", "production"];

  if (loading && !hasLoadedOnce.current) {
    return (
      <LoadingSpinner
        message="Loading files..."
        subMessage="Fetching uploaded files for this agent"
      />
    );
  }

  if (data) hasLoadedOnce.current = true;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {/* Info banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          mb: 2,
          borderRadius: "10px",
          bgcolor: agentId ? INDIGO[50] : SLATE[50],
          border: `1px solid ${agentId ? INDIGO[100] : SLATE[200]}`,
        }}
      >
        <InfoIcon sx={{ fontSize: "1rem", color: agentId ? INDIGO[400] : SLATE[400], mt: "2px", flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: agentId ? INDIGO[800] : SLATE[700], lineHeight: 1.4 }}>
            {agentId
              ? "Files are indexed through the same pipeline as website content"
              : "Client-level view — read only"}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: agentId ? INDIGO[600] : SLATE[500], mt: "2px" }}>
            {agentId
              ? "Each uploaded PDF is processed and indexed as a knowledge source. Its indexing status follows the same workflow as website URLs — you can track progress in the Status column below."
              : "This is a consolidated view of all uploaded files across all agents in this client. To upload or manage files, navigate to a specific agent's Knowledge Base."}
          </Typography>
        </Box>
      </Box>

      {/* Upload component — button hidden, triggered via TopBar ref */}
      {agentId && (
        <FileUploadPreSignedUrl
          field={{ name: "fileUpload", title: "+ Add Files", multiple: true }}
          accept=".pdf"
          presignUrlEndpoint={FILES_PRESIGNED_URL_ENDPOINT}
          completeUploadEndpoint={FILES_COMPLETE_UPLOAD_ENDPOINT}
          cloudFrontBaseUrl={FILES_CLOUDFRONT_BASE_URL}
          agentId={agentId}
          triggerRef={uploadTriggerRef}
          hideButton
          onPresignSuccess={refreshData}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <SearchInput
          value={searchQuery}
          onChange={(q) => setSearchQuery(q)}
          placeholder="Search files..."
        />
      </Box>

      {/* Card */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.04)",
        }}
      >
        {/* Column Headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: COL_TEMPLATE,
            px: 3,
            py: "9px",
            borderBottom: `1px solid ${SLATE[200]}`,
            bgcolor: SLATE[50],
          }}
        >
          {["File", "Source URL", "Status", "Tags", "AI Extraction", ""].map((col) => (
            <Typography
              key={col}
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: SLATE[500],
                textTransform: "uppercase",
                letterSpacing: "0.055em",
              }}
            >
              {col}
            </Typography>
          ))}
        </Box>

        {/* Body */}
        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} sx={{ color: INDIGO[500] }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#ef4444" }}>
              Failed to load files. Please try again.
            </Typography>
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>📄</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
              No files uploaded yet
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
              {agentId
                ? "Upload PDF files to build the knowledge base for this agent."
                : "No files have been uploaded yet. Go to an agent's Knowledge Base to upload files."}
            </Typography>
            {agentId && (
              <Button
                variant="contained"
                onClick={() => uploadTriggerRef.current?.()}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                + Add Files
              </Button>
            )}
          </Box>
        ) : (
          filteredFiles.map((file, idx) => {
            const isEditingTags = editingTagsFileId === file.id;

            return (
              <Box
                key={file.id}
                onClick={!agentId || isEditingTags ? undefined : () => {
                  setEditingTagsFileId(file.id);
                  setEditingTags(file.tags || []);
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: COL_TEMPLATE,
                  px: 3,
                  py: isEditingTags ? "10px" : "14px",
                  alignItems: "center",
                  borderBottom:
                    idx < filteredFiles.length - 1
                      ? `1px solid ${SLATE[100]}`
                      : "none",
                  bgcolor: isEditingTags ? INDIGO[50] : "#ffffff",
                  borderLeft: isEditingTags ? `3px solid ${INDIGO[400]}` : "3px solid transparent",
                  transition: "background-color 0.15s, border-color 0.15s",
                  cursor: !agentId || isEditingTags ? "default" : "pointer",
                  "&:hover": {
                    bgcolor: isEditingTags ? INDIGO[50] : (agentId ? SLATE[50] : "#ffffff"),
                  },
                }}
              >
                {/* File Name + added by */}
                <Box sx={{ minWidth: 0, pr: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: SLATE[800],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={file.file_name}
                  >
                    {file.file_name || "—"}
                  </Typography>
                  {file.added_by && (
                    <Typography sx={{ fontSize: "0.72rem", color: SLATE[400], mt: "2px", letterSpacing: "0.01em" }}>
                      by {file.added_by}
                    </Typography>
                  )}
                </Box>

                {/* Source URL */}
                <Box sx={{ minWidth: 0, pr: 2 }}>
                  {file.url && file.url !== "NOT GENERATED YET" ? (
                    <Tooltip title={file.url} placement="top">
                      <Link
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: "0.8125rem",
                          color: INDIGO[600],
                          textDecoration: "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {file.url}
                      </Link>
                    </Tooltip>
                  ) : (
                    <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                  )}
                </Box>

                {/* Status */}
                <Box>
                  <StatusChip status={file.status} />
                </Box>

                {/* Tags */}
                <Box onClick={(e) => e.stopPropagation()}>
                  {isEditingTags ? (
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={tagOptions}
                      value={editingTags}
                      onChange={(_, val) => setEditingTags(val)}
                      renderTags={(val, getTagProps) =>
                        val.map((opt, i) => (
                          <Chip
                            key={opt}
                            label={opt}
                            size="small"
                            {...getTagProps({ index: i })}
                            sx={{
                              height: 20,
                              fontSize: "0.6875rem",
                              bgcolor: INDIGO[50],
                              color: INDIGO[700],
                              "& .MuiChip-label": { px: "6px" },
                              "& .MuiChip-deleteIcon": { color: INDIGO[400] },
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={editingTags.length === 0 ? "Add tags..." : ""}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "7px",
                              fontSize: "0.8125rem",
                              bgcolor: "#fff",
                              "& fieldset": { borderColor: INDIGO[300] },
                              "&:hover fieldset": { borderColor: INDIGO[400] },
                              "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
                            },
                          }}
                        />
                      )}
                    />
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", minHeight: 24, alignItems: "center" }}>
                      {(file.tags || []).length === 0 ? (
                        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                      ) : (
                        <>
                          {(file.tags || []).slice(0, 2).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.6875rem",
                                bgcolor: INDIGO[50],
                                color: INDIGO[600],
                                fontWeight: 500,
                                border: `1px solid ${INDIGO[100]}`,
                                borderRadius: "6px",
                                "& .MuiChip-label": { px: "7px" },
                              }}
                            />
                          ))}
                          {(file.tags || []).length > 2 && (
                            <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], alignSelf: "center" }}>
                              +{file.tags.length - 2}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>

                {/* LLM Extraction */}
                <Box onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={file.use_llm_extraction || false}
                    onChange={() =>
                      agentId && handleLlmExtractionToggle(file.id, file.use_llm_extraction, file.url)
                    }
                    disabled={!agentId}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] },
                    }}
                  />
                </Box>

                {/* Actions */}
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isEditingTags && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleSaveTags(file.id, editingTags);
                          setEditingTagsFileId(null);
                        }}
                        sx={{ color: INDIGO[600], bgcolor: INDIGO[100], borderRadius: "7px", p: "5px", "&:hover": { bgcolor: INDIGO[200] } }}
                      >
                        <CheckIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEditingTagsFileId(null)}
                        sx={{ color: SLATE[500], bgcolor: SLATE[100], borderRadius: "7px", p: "5px", "&:hover": { bgcolor: SLATE[200] } }}
                      >
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </>
                  )}
                </Box>

              </Box>
            );
          })
        )}
      </Box>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={cancelLlmExtractionToggle}
        onConfirm={confirmLlmExtractionToggle}
        title={selectedFile?.currentValue ? "Disable LLM Extraction" : "Enable LLM Extraction"}
        content={
          selectedFile?.currentValue
            ? "LLM extraction will be turned off for this file. It will be re-queued for standard processing."
            : "LLM extraction will be enabled for this file. It will be re-queued for enhanced text processing using the language model."
        }
      />
    </Box>
  );
}
